/**
 * MobileActionBarContainer - Logic for the action bar (+ button)
 *
 * Manages the picker state for the "+" button.
 * Tapping "+" opens a picker with "Add a report", "Add to list",
 * and for manual players, "Edit player" and "Delete player" options.
 * On mobile: picker renders as an Actionsheet (bottom sheet).
 * On desktop: picker renders as a Popover (portal-based dropdown below the + button).
 * Navigation back is handled by the breadcrumb — no close button needed.
 * @remarks This is a Container component - handles business logic and state
 */

import { ChevronDown, Plus } from "lucide-react-native";
import { useCallback, useState } from "react";
import { Pressable } from "@/components/atoms/Pressable";

import { Icon } from "@/components/atoms/Icon";

import MobileActionBarView from "./MobileActionBarView";

/**
 * Get pill background class for the plus button
 */
const getAddPillClass = (): string =>
  "flex-row items-center rounded-full bg-surface-card/80 py-1 pl-3 pr-2";

/**
 * Get icon class for the plus icon
 */
const getPlusIconClass = (): string => "text-content-primary";

/**
 * Get chevron class for the dropdown indicator
 */
const getChevronClass = (): string => "text-content-muted";

/**
 * Props for MobileActionBarContainer
 */
interface MobileActionBarContainerProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Callback to open the watchlist actionsheet */
  readonly onAddToList?: () => void;
  /** Callback to navigate to the report creation page */
  readonly onAddReport?: () => void;
  /** Callback when edit player is pressed — manual players only */
  readonly onEditPlayer?: () => void;
  /** Callback when delete player is pressed — manual players only */
  readonly onDeletePlayer?: () => void;
  /** Callback for "Transfer reports & notes" — admin users on manual players only */
  readonly onTransferReports?: () => void;
  /** Whether the transfer reports action is disabled (0 reports and 0 notes) */
  readonly isTransferDisabled?: boolean;
  /** Whether the screen is mobile sized — controls picker overlay type */
  readonly isSmallScreen?: boolean;
}

/**
 * MobileActionBarContainer manages picker state and creates the Popover trigger
 * @param props - Container props
 * @param props.isDark - Whether dark mode is active
 * @param props.onAddToList - Callback to open the watchlist actionsheet
 * @param props.onAddReport - Callback to navigate to report creation
 * @param props.onEditPlayer - Callback when edit player is pressed (manual players only)
 * @param props.onDeletePlayer - Callback when delete player is pressed (manual players only)
 * @param props.onTransferReports - Callback for "Transfer reports & notes" (admin on manual players)
 * @param props.isTransferDisabled - Whether the transfer action is disabled (0 reports and 0 notes)
 * @param props.isSmallScreen - Whether mobile sized (Actionsheet vs Popover)
 * @returns MobileActionBarView with picker state
 */
const MobileActionBarContainer = ({
  isDark,
  onAddToList,
  onAddReport,
  onEditPlayer,
  onDeletePlayer,
  onTransferReports,
  isTransferDisabled,
  isSmallScreen = false,
}: MobileActionBarContainerProps) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const handlePickerOpen = useCallback(() => {
    setIsPickerOpen(true);
  }, []);

  const handlePickerClose = useCallback(() => {
    setIsPickerOpen(false);
  }, []);

  const handleAddReport = useCallback(() => {
    setIsPickerOpen(false);
    onAddReport?.();
  }, [onAddReport]);

  const handleAddToList = useCallback(() => {
    setIsPickerOpen(false);
    onAddToList?.();
  }, [onAddToList]);

  const handleEditPlayer = useCallback(() => {
    setIsPickerOpen(false);
    onEditPlayer?.();
  }, [onEditPlayer]);

  const handleDeletePlayer = useCallback(() => {
    setIsPickerOpen(false);
    onDeletePlayer?.();
  }, [onDeletePlayer]);

  const handleTransferReports = useCallback(() => {
    setIsPickerOpen(false);
    onTransferReports?.();
  }, [onTransferReports]);

  /** Render the + pill button as a Popover trigger for desktop */
  const renderPickerTrigger = useCallback(
    (triggerProps: Record<string, unknown>) => (
      <Pressable
        {...triggerProps}
        className={getAddPillClass()}
        accessibilityRole="button"
        accessibilityLabel="Add action"
      >
        <Icon as={Plus} size="sm" className={getPlusIconClass()} />
        <Icon as={ChevronDown} size="2xs" className={getChevronClass()} />
      </Pressable>
    ),
    []
  );

  return (
    <MobileActionBarView
      isDark={isDark}
      showPlusButton={Boolean(
        onAddToList ||
        onAddReport ||
        onEditPlayer ||
        onDeletePlayer ||
        onTransferReports
      )}
      onPlusPress={handlePickerOpen}
      isPickerOpen={isPickerOpen}
      onPickerClose={handlePickerClose}
      onAddReport={onAddReport ? handleAddReport : undefined}
      onAddToList={onAddToList ? handleAddToList : undefined}
      onEditPlayer={onEditPlayer ? handleEditPlayer : undefined}
      onDeletePlayer={onDeletePlayer ? handleDeletePlayer : undefined}
      onTransferReports={onTransferReports ? handleTransferReports : undefined}
      isTransferDisabled={isTransferDisabled}
      isSmallScreen={isSmallScreen}
      renderPickerTrigger={renderPickerTrigger}
    />
  );
};

MobileActionBarContainer.displayName = "MobileActionBarContainer";

export default MobileActionBarContainer;
